import styled from "@emotion/styled";
import { useCallback, useState, type MouseEventHandler } from "react";
import useResizeObserver from "use-resize-observer";

import { useDimensions } from "../perspective/DimensionsContext";
import { usePan } from "../perspective/PanContext";
import { useZoom } from "../perspective/ZoomContext";
import type { TouchTargetHandler } from "../touch";
import { TouchTarget } from "../touch";
import { isHorizontalPlacement } from "./helpers";
import { useTray } from "./TrayContext";
import type { Placement } from "./types";

const MARGIN = 32;

const mapPlacementToDirection: Record<Placement, string> = {
  top: "row",
  bottom: "row",
  left: "column",
  right: "column",
};

const TrayContainer = styled.div<{ show: boolean; active: boolean; placement: Placement }>`
  display: flex;

  flex-direction: ${({ placement }) => mapPlacementToDirection[placement]};
  justify-content: center;
  align-items: center;

  position: absolute;

  top: 0;
  left: 0;

  padding: ${({ placement }) => (isHorizontalPlacement(placement) ? "8px 16px" : "16px 12px")};

  background-color: rgba(0, 0, 0, 0.08);
  border-radius: 1rem;

  opacity: ${({ show }) => (show ? 0.7 : 0)};
  pointer-events: ${({ active }) => (active ? "auto" : "none")};

  transition:
    opacity 400ms ease-in-out 300ms,
    transform 300ms ease-in-out;
  z-index: 3;

  @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
    backdrop-filter: blur(8px);
  }
`;

const TrayItemsContainer = styled.div<{ placement: Placement }>`
  display: flex;
  flex-direction: ${({ placement }) => mapPlacementToDirection[placement]};
  gap: ${({ placement }) => (isHorizontalPlacement(placement) ? "16px" : "12px")};
  justify-content: center;
  align-items: center;
`;

type TrayProps = {
  containerDimensions: Pick<DOMRect, "height" | "width">;
};

export const Tray = ({ containerDimensions }: TrayProps) => {
  const { pan } = usePan();
  const { zoom } = useZoom();
  const { width, height } = useDimensions();
  const { show, active, placement, items } = useTray();

  const [hover, setHover] = useState(false);

  const { width: trayWidth = 0, height: trayHeight = 0, ref } = useResizeObserver();

  const isHorizontal = isHorizontalPlacement(placement);

  const adjustment = {
    x: isHorizontal
      ? 0
      : ((placement === "left" ? -1 : 1) * (containerDimensions.width / 2 - trayWidth - MARGIN)) / zoom,
    y: isHorizontal
      ? ((placement === "top" ? -1 : 1) * (containerDimensions.height / 2 - trayHeight - MARGIN)) / zoom
      : 0,
  };

  const x = (width - trayWidth) / 2 + adjustment.x - pan.x;
  const y = (height - trayHeight) / 2 + adjustment.y - pan.y;

  const handleTouchMoveOn: TouchTargetHandler = () => {
    setHover(true);
    return "stopPropagation";
  };

  const handleTouchMoveOff: TouchTargetHandler = () => {
    setHover(false);
    return "stopPropagation";
  };

  const handleMouseClick: MouseEventHandler<HTMLDivElement> = useCallback((event) => event.stopPropagation(), []);

  return (
    <TrayContainer
      ref={ref}
      active={active}
      show={show}
      placement={placement}
      style={{ transform: `translate(${x}px, ${y}px) scale(${(hover ? 2 : 1) / zoom})` }}
      onClick={handleMouseClick}
      data-test="tray-container"
    >
      <TouchTarget
        onTouchMoveOn={handleTouchMoveOn}
        onTouchMoveOff={handleTouchMoveOff}
        onTouchEnd={handleTouchMoveOff}
        dataTest="list"
      >
        <TrayItemsContainer placement={placement}>{items}</TrayItemsContainer>
      </TouchTarget>
    </TrayContainer>
  );
};
